import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { getPlans } from '../../api/auth'
import { addClient } from '../../api/practice'
import ErrorBanner from '../../components/ErrorBanner'
import HelpTip from '../../components/HelpTip'
import { BackLink, DocHeader } from '../../components/PageParts'
import { Field } from '../master/masterShared'

const PAYS_HELP = 'The company pays for its own plan, as if it had signed up itself: a free trial first, then its owner chooses to pay under Settings → Plan & billing, where they can also add add-ons, users and locations. Your practice stays free.'

/** A practice sets up ASYS for a company it looks after. */
export default function AddClient() {
  const nav = useNavigate()
  const [catalogue, setCatalogue] = useState(null)
  const [form, setForm] = useState({ company_name: '', business_type: '', plan: 'invoice_basic', owner_name: '', owner_email: '' })
  const [fields, setFields] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => { getPlans().then(setCatalogue).catch(() => setError('Couldn’t load the plans.')) }, [])
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }))
    setFields((e) => (e[k] ? { ...e, [k]: undefined } : e))
  }

  const save = async (e) => {
    e && e.preventDefault()
    setSaving(true); setError(''); setFields({})
    try {
      const r = await addClient({ ...form, owner_email: form.owner_email.trim().toLowerCase() })
      nav('/practice', { replace: true, state: { added: r } })
    } catch (err) {
      setFields(err.fields || {})
      setError(Object.keys(err.fields || {}).length ? '' : (err.message || 'Couldn’t add the client.'))
      setSaving(false)
    }
  }

  const plans = catalogue?.plans || []
  const tier = (p) => catalogue?.tiers?.find((t) => t.id === p.tier)?.label || p.tier
  const chosen = plans.find((p) => p.id === form.plan)
  return (
    <form className="ci-form-page" onSubmit={save} noValidate>
      <DocHeader back={<BackLink to="/practice" label="Clients" />} title="Add a client"
        meta="Set up ASYS for a company you look after. Your practice gets Full books; the owner can change that later." />
      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">The company <HelpTip text={PAYS_HELP} /></div>
        <div className="ci-form-meta-top two">
          <Field label="Company name" error={fields.company_name}>
            <input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} autoFocus />
          </Field>
          <Field label="Who owns it?" error={fields.business_type} hint="Sets up the owner’s accounts: capital and drawings for an owner, share capital and dividends for a company.">
            <select value={form.business_type} onChange={(e) => set('business_type', e.target.value)}>
              <option value="">Choose one…</option>
              <option value="sole">A sole owner or partnership</option>
              <option value="company">A company with shares (LLC, Ltd, Inc)</option>
            </select>
          </Field>
        </div>
        <div className="ci-form-meta-top two">
          <Field label="Plan" error={fields.plan}
            hint={chosen && catalogue ? `Free for ${catalogue.trial_days} days, then $${chosen.price}/month, paid by the company.` : undefined}>
            <select value={form.plan} onChange={(e) => set('plan', e.target.value)} disabled={!catalogue}>
              {(catalogue?.lines || []).map((l) => (
                <optgroup key={l.id} label={l.label}>
                  {plans.filter((p) => p.line === l.id).map((p) => <option key={p.id} value={p.id}>{l.label} {tier(p)} · ${p.price}/month</option>)}
                </optgroup>
              ))}
            </select>
          </Field>
        </div>
      </div>

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">The owner</div>
        <div className="ci-form-meta-top two">
          <Field label="Their name" error={fields.owner_name}>
            <input value={form.owner_name} onChange={(e) => set('owner_name', e.target.value)} autoComplete="off" />
          </Field>
          <Field label="Their email" error={fields.owner_email} hint="They sign in with this email as their username. We email them a link to choose a password.">
            <input type="email" value={form.owner_email} onChange={(e) => set('owner_email', e.target.value)} placeholder="name@company.com" autoComplete="off" />
          </Field>
        </div>
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="submit" className="ci-btn ci-btn-primary" disabled={saving || !catalogue}><Plus size={14} />{saving ? 'Setting it up…' : 'Add the client'}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/practice')}>Cancel</button>
        </div>
      </div>
    </form>
  )
}
